import React, { useEffect, useMemo, useState } from "react";
import { geoCentroid } from "d3-geo";
import { ComposableMap, Geographies, Geography, Marker, Annotation } from "react-simple-maps";
import ReactTooltip from "react-tooltip";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import * as d3 from "d3";
import PropTypes from "prop-types";
import { useStyles, tooltipBkgColor, topTipStyle } from "../shared/MapTooltip";
import "../../styles/map.css";
import legendConfig from "../../utils/legendConfig.json";
import DrawLegend from "../shared/DrawLegend";
import { ShortFormat } from "../shared/ConvertionFormats";
import { STATE_TOPOJSON_URL, loadTopoJson } from "../../utils/countyGeo";
import { ACCENT_BLUE, NO_DATA_GRAY, TEXT_DARKER, TEXT_DIM, TEXT_FAINT, WHITE } from "../shared/colors";

const offsets = {
    VT: [50, -8],
    NH: [34, 2],
    MA: [30, -1],
    RI: [28, 2],
    CT: [35, 10],
    NJ: [34, 1],
    DE: [33, 0],
    MD: [47, 10],
    DC: [49, 21]
};

const MapChart = (props) => {
    const { setReactTooltipContent, stateRecordsByName, stateAbbrevByVal, stateTopology, colorScale } = props;
    const classes = useStyles();
    return (
        <div data-tip="">
            {stateTopology ? (
                <ComposableMap projection="geoAlbersUsa">
                    <Geographies geography={stateTopology}>
                        {({ geographies }) => (
                            <>
                                {geographies.map((geo) => {
                                    const record = stateRecordsByName[geo.properties.name];
                                    if (record === undefined || record.length === 0) {
                                        return null;
                                    }
                                    const totalPaymentInDollars = record.totalPaymentInDollars;
                                    const hoverContent = (
                                        <div className="map_tooltip">
                                            <div className={classes.tooltip_header}>
                                                <b>{geo.properties.name}</b>
                                            </div>
                                            <table className={classes.tooltip_table}>
                                                <tbody key={geo.properties.name}>
                                                    {record.years &&
                                                        Object.entries(record.years).map(([each_year, data], idx) => (
                                                            <tr key={each_year}>
                                                                <td
                                                                    className={
                                                                        idx === 0
                                                                            ? classes.tooltip_topcell_left
                                                                            : classes.tooltip_regularcell_left
                                                                    }
                                                                >
                                                                    {each_year} Benefit:
                                                                </td>
                                                                <td
                                                                    className={
                                                                        idx === 0
                                                                            ? classes.tooltip_topcell_right
                                                                            : classes.tooltip_regularcell_right
                                                                    }
                                                                >
                                                                    $
                                                                    {ShortFormat(
                                                                        data.totalPaymentInDollars,
                                                                        undefined,
                                                                        2
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    {/* Divider line row */}
                                                    <tr>
                                                        <td colSpan={2}>
                                                            <hr
                                                                style={{
                                                                    margin: "4px 0",
                                                                    border: "0",
                                                                    borderTop: "1px solid #ccc"
                                                                }}
                                                            />
                                                        </td>
                                                    </tr>
                                                    <tr style={topTipStyle}>
                                                        <td className={classes.tooltip_bottomcell_left}>Benefits:</td>
                                                        <td className={classes.tooltip_bottomcell_right}>
                                                            ${ShortFormat(totalPaymentInDollars, undefined, 2)}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    );
                                    const fillColour = () => {
                                        if (totalPaymentInDollars) {
                                            if (totalPaymentInDollars !== 0) return colorScale(totalPaymentInDollars);
                                            return NO_DATA_GRAY;
                                        }
                                        return NO_DATA_GRAY;
                                    };
                                    return (
                                        <Geography
                                            key={geo.rsmKey}
                                            geography={geo}
                                            onMouseEnter={() => {
                                                setReactTooltipContent(hoverContent);
                                            }}
                                            onMouseLeave={() => {
                                                setReactTooltipContent("");
                                            }}
                                            fill={fillColour()}
                                            stroke="#FFF"
                                            style={{
                                                default: { stroke: WHITE, strokeWidth: 0.75, outline: "none" },
                                                hover: {
                                                    stroke: TEXT_DARKER,
                                                    strokeWidth: 2,
                                                    outline: "none"
                                                },
                                                pressed: {
                                                    fill: ACCENT_BLUE,
                                                    outline: "none"
                                                }
                                            }}
                                        />
                                    );
                                })}
                                {geographies.map((geo) => {
                                    const centroid = geoCentroid(geo);
                                    const cur = stateAbbrevByVal[String(geo.id)];
                                    return (
                                        <g key={`${geo.rsmKey}-name`}>
                                            {cur &&
                                                centroid[0] > -160 &&
                                                centroid[0] < -67 &&
                                                (Object.keys(offsets).indexOf(cur.id) === -1 ? (
                                                    <Marker coordinates={centroid}>
                                                        <text y="2" fontSize={14} textAnchor="middle">
                                                            {cur.id}
                                                        </text>
                                                    </Marker>
                                                ) : (
                                                    <Annotation
                                                        subject={centroid}
                                                        dx={offsets[cur.id][0]}
                                                        dy={offsets[cur.id][1]}
                                                    >
                                                        <text x={4} fontSize={14} alignmentBaseline="middle">
                                                            {cur.id}
                                                        </text>
                                                    </Annotation>
                                                ))}
                                        </g>
                                    );
                                })}
                            </>
                        )}
                    </Geographies>
                </ComposableMap>
            ) : (
                <Box display="flex" justifyContent="center" sx={{ pt: 1 }}>
                    <h1>Loading Map Data...</h1>
                </Box>
            )}
        </div>
    );
};

MapChart.propTypes = {
    setReactTooltipContent: PropTypes.func,
    stateTopology: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
};

const Title1TotalMap = ({
    program: _program,
    attribute: _attribute,
    year,
    statePerformance,
    stateCodes,
    allStates,
    mapColor
}: {
    program: string;
    attribute: any;
    year: string;
    statePerformance: any;
    stateCodes: any;
    allStates: any;
}): JSX.Element => {
    const [content, setContent] = useState("");
    const [stateTopology, setStateTopology] = useState<Record<string, unknown> | null>(null);
    const [topologyLoadAttempted, setTopologyLoadAttempted] = useState(false);
    const quantizeArray: number[] = [];
    const zeroPoints = [];
    statePerformance[year].forEach((value) => {
        const ACur = value;
        const key = "totalPaymentInDollars";
        quantizeArray.push(ACur[key]);
        ACur[key] === 0 && zeroPoints.push(value.state);
        return null;
    });
    const category = "Title I Total";
    const maxValue = Math.max(...quantizeArray);
    const customScale = legendConfig[category];
    const colorScale = d3.scaleThreshold(customScale, mapColor);
    const stateRecordsByName = useMemo(() => {
        const mapped: Record<string, any> = {};
        statePerformance[year].forEach((record) => {
            const stateName = stateCodes[record.state];
            if (stateName) {
                mapped[stateName] = record;
            }
        });
        return mapped;
    }, [stateCodes, statePerformance, year]);
    const stateAbbrevByVal = useMemo(() => {
        const mapped: Record<string, any> = {};
        allStates.forEach((state) => {
            if (state?.val !== undefined && state?.id) {
                mapped[String(state.val)] = state;
            }
        });
        return mapped;
    }, [allStates]);
    const stateGeographySource = stateTopology || (topologyLoadAttempted ? STATE_TOPOJSON_URL : null);

    useEffect(() => {
        let active = true;
        loadTopoJson(STATE_TOPOJSON_URL)
            .then((topology) => {
                if (!active) {
                    return;
                }
                setStateTopology(topology);
            })
            .catch(() => undefined)
            .finally(() => {
                if (active) {
                    setTopologyLoadAttempted(true);
                }
            });
        return () => {
            active = false;
        };
    }, []);

    const classes = useStyles();
    return (
        <div>
            <Box display="flex" justifyContent="center">
                {maxValue !== 0 ? (
                    <DrawLegend
                        colorScale={colorScale}
                        title={titleElement(category, year)}
                        programData={quantizeArray}
                        prepColor={mapColor}
                        isRatio={false}
                        notDollar={false}
                        emptyState={[]}
                    />
                ) : (
                    <div>
                        {titleElement(category, year)}
                        <Box display="flex" justifyContent="center">
                            <Typography sx={{ color: TEXT_FAINT, fontWeight: 700 }}>
                                {category} data in {year} is unavailable for all states.
                            </Typography>
                        </Box>
                    </div>
                )}
            </Box>

            <MapChart
                setReactTooltipContent={setContent}
                stateRecordsByName={stateRecordsByName}
                stateAbbrevByVal={stateAbbrevByVal}
                stateTopology={stateGeographySource}
                colorScale={colorScale}
            />

            <div className="tooltip-container">
                <ReactTooltip className={`${classes.customized_tooltip} tooltip`} backgroundColor={tooltipBkgColor}>
                    {content}
                </ReactTooltip>
            </div>
        </div>
    );
};
const titleElement = (attribute, year): JSX.Element => {
    return (
        <Box>
            <Typography noWrap variant="h6">
                Total Commodities Programs (Title I) from <strong>{year}</strong>
            </Typography>{" "}
            <Typography noWrap style={{ fontSize: "0.5em", color: TEXT_DIM, textAlign: "center" }}>
                <i>In any state that appears in gray, there is no available data</i>
            </Typography>
        </Box>
    );
};
export default Title1TotalMap;
